
import { Component, Prop, Vue } from 'vue-property-decorator';
import { PERMISSION } from '@/permissions';
import { Action, Getter } from 'vuex-class';
import { toErrorMessage } from '@/utils/errorHandling';
import Toast from '@/utils/toast';
import ExpandableList from '@/components/shared/ExpandableList.vue';
import TypeToConfirmModal from '@/components/shared/TypeToConfirmModal.vue';
import InfoBanner from '@/components/shared/suites/InfoBanner.vue';
import TextButton from '@/components/shared/suites/TextButton.vue';

const namespace: string = 'menus';

interface StartReplicationPayload {
	menuIds: number[];
	restaurantIds: string[];
	copyRotationalMenus: boolean;
}

@Component<ReplicateMenusStartForm>({
	components: {
		ExpandableList,
		InfoBanner,
		TextButton,
		TypeToConfirmModal
	}
})
export default class ReplicateMenusStartForm extends Vue {
	@Prop({ type: Array, required: false, default: () => [] }) private initialRestaurantIds!: string[];
	@Action('startJob', { namespace: 'menuReplication' }) private startJob!: (payload: StartReplicationPayload) => Promise<void>;
	@Action('getMenuIdsFromRotationalMenuItems', { namespace }) private getMenuIdsFromRotationalMenuItems!: () => Promise<number[]>;
	@Action('getRestaurantsWithMenus', { namespace: 'auth' }) private getRestaurantsWithMenus!: (payload: { ids: string[], k12: boolean }) => Promise<Tenant[]>;
	@Getter('getMenus', { namespace }) private menus!: Menu[];
	@Getter('getRestaurants', { namespace: 'auth' }) private restaurants!: Tenant[];
	@Getter('getTenantId', { namespace: 'auth' }) private tenantId!: string;
	@Getter('isK12Location', { namespace: 'auth' }) private isK12Location!: boolean;
	@Getter('hasActiveJob', { namespace: 'menuReplication' }) private hasActiveJob!: boolean;
	@Getter('hasPermission', { namespace: 'auth' }) private hasPermission!: (permission: PERMISSION) => boolean;

	private PERMISSION = PERMISSION;
	private isStarting: boolean = false;
	private warningModalOpen: boolean = false;
	private copyRotationalMenus: boolean = false;
	private restaurantList: Tenant[] = [];
	private menuList: Menu[] = [];
	private restaurantIdList: string[] = [];

	private get overwriteMenus(): boolean {
		return this.isK12Location && this.copyRotationalMenus;
	}

	private get canStart(): boolean {
		return !!this.menuList.length && !!this.restaurantIdList.length && !this.isStarting && !this.hasActiveJob;
	}

	/**
	 * Filter out any marketplace menus, these menus will cause an error when trying to replicate them
	 * This is because there won't be an associated menu_group in the target database
	 *
	 * @return {Menu[]}
	 */
	private get filteredMenus(): Menu[] {
		return this.menus.filter((menu: Menu) => !menu.menu_group_id);
	}

	private async created(): Promise<void> {
		// Pre-populate from the retry-failed flow (parent passes the failed target IDs as a prop).
		// Menus are not persisted on the job, so only targets are restored; the user re-picks menus.
		if (this.initialRestaurantIds.length) {
			this.restaurantIdList = [...this.initialRestaurantIds];
		}
		await this.fetchRestaurantsList();
	}

	/**
	 * Fetches the list of locations to copy menus to.
	 *
	 * @return {Promise<void>}
	 */
	private async fetchRestaurantsList(): Promise<void> {
		try {
			this.$emit('loading', true);
			// Filter out the current location from the list
			const idList = this.restaurants
				.filter((restaurant: Tenant) => restaurant.id !== this.tenantId)
				.map((restaurant: Tenant) => restaurant.id);
			this.restaurantList = await this.getRestaurantsWithMenus({ ids: idList, k12: this.isK12Location });
		}
		catch (error) {
			Toast.error(this.$t('menu.replicate.error_fetching_locations', { errorMessage: toErrorMessage(error) }));
		}
		finally {
			this.$emit('loading', false);
		}
	}

	/**
	 * Push or remove the menu depending on if it was selected or unselected.
	 *
	 * @param {boolean} value
	 * @param {Menu} menu
	 * @return {void}
	 */
	private onMenuSelected(value: boolean, menu: Menu): void {
		value ? this.menuList.push(menu) : this.menuList.splice(this.menuList.indexOf(menu), 1);
	}

	/**
	 * Push or remove the location depending on if it was selected or unselected.
	 *
	 * @param {boolean} value
	 * @param {string} id
	 * @return {void}
	 */
	private onRestaurantSelected(value: boolean, id: string): void {
		value ? this.restaurantIdList.push(id) : this.restaurantIdList.splice(this.restaurantIdList.indexOf(id), 1);
	}

	private closeWarningModal(): void {
		this.warningModalOpen = false;
	}

	/**
	 * When turning on the copyRotationalMenus switch, fetch the ids for the menus used for rotational
	 * menus, then update the menuList to only check these menus for replication.
	 *
	 * @return {Promise<void>}
	 */
	private async handleCopyRotationalMenus(): Promise<void> {
		if (this.copyRotationalMenus) {
			const menusToSelect = await this.getMenuIdsFromRotationalMenuItems();
			this.menuList = this.menus.filter((menu: Menu) => menu.id && menusToSelect.includes(menu.id));
		}
		else {
			this.menuList = [];
		}
	}

	/**
	 * Kick off an async replication job for the selected menus + target locations. The parent
	 * container swaps to the live progress view once the job's active state is set by the store.
	 *
	 * @return {Promise<void>}
	 */
	private async start(): Promise<void> {
		if (!this.canStart) {
			return;
		}
		this.isStarting = true;
		try {
			await this.startJob({
				menuIds: this.menuList.map((menu: Menu) => menu.id!).filter(Boolean),
				restaurantIds: this.restaurantIdList,
				copyRotationalMenus: this.copyRotationalMenus
			});
		}
		catch (error) {
			Toast.error(toErrorMessage(error));
		}
		finally {
			this.isStarting = false;
			this.closeWarningModal();
		}
	}
}
